import React from 'react';
import { Spinner, Tooltip } from 'design-system';
import { useObjectPreview } from 'corp/hooks/smartId/useObjectPreview';
import { INamedObjectTypes, ITaskReviewPreviewJson } from '@scale/shared/types/lookup';
import IsomorphicLink from 'corp/components/UI/IsomorphicLink';
import moment from 'moment';
import { ReviewLevel, TaskReviewOutcome } from '@scale/shared/defaults';

const colorOfTaskStatus: Record<TaskReviewOutcome, string> = {
  [TaskReviewOutcome.CorpPromoted]: 'var(--color-ScaleBlue)',
  [TaskReviewOutcome.CorpRecalled]: 'var(--color-ScaleBlue)',
  [TaskReviewOutcome.Accepted]: 'var(--color-RemoGreen)',
  [TaskReviewOutcome.Rejected]: 'var(--color-RemoRed)',
  [TaskReviewOutcome.Fixed]: 'var(--color-RemoGold)',
  [TaskReviewOutcome.Uncertain]: 'black',
};

const TaskReviewPreview: React.FC<{ id: string }> = ({ id }) => {
  const {
    value: taskReview,
    loading,
    error,
  } = useObjectPreview<ITaskReviewPreviewJson>(id, INamedObjectTypes.TaskReview);
  if (error) return <span>Sorry, there was an error</span>;
  if (loading) return <Spinner />;
  const reviewLevelDisplay = (reviewLevel: ReviewLevel) =>
    reviewLevel === -1 ? 'Attempt' : 'L' + reviewLevel;

  return (
    <div className="taskreview-preview">
      <style jsx>{`
        .taskreview-preview__banner {
          font-weight: bold;
          padding: 10px;
          display: flex;
          align-items: center;
        }

        .taskreview-preview__thing-list > div {
          display: flex;
        }

        .taskreview-preview__thing-list strong {
          color: #666;
          margin-right: 10px;
          min-width: 120px;
          text-align: right;
        }
      `}</style>
      {taskReview && (
        <>
          <div className="taskreview-preview__banner">
            <span
              style={{
                fontSize: 'smaller',
                backgroundColor: colorOfTaskStatus[taskReview.reviewOutcome],
                color: 'white',
                borderRadius: '14px',
                height: '28px',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 10px',
              }}
            >
              {taskReview.reviewOutcome}
            </span>
            <h3 style={{ margin: '0 5px' }}>{id}</h3>
          </div>
          <div className="taskreview-preview__body">
            <div className="taskreview-preview__thing-list">
              <div>
                <strong>Lookup</strong>
                <IsomorphicLink target="_blank" rel="noopener noreferrer" href={`/lookup/${id}`}>
                  {id}{' '}
                </IsomorphicLink>
              </div>

              <div>
                <strong>Subtask</strong>
                <IsomorphicLink
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`/lookup/${taskReview.subtask}`}
                >
                  {taskReview.subtask}
                </IsomorphicLink>
              </div>
              <div>
                <strong>Task</strong>
                <IsomorphicLink
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`/lookup/${taskReview.task}`}
                >
                  {taskReview.task}
                </IsomorphicLink>
              </div>

              {typeof taskReview.attemptReviewLevel !== 'undefined' && (
                <div>
                  <strong>Reviewed At Level</strong>
                  <span>{reviewLevelDisplay(taskReview.attemptReviewLevel)}</span>
                </div>
              )}
              {!!taskReview.reviewedBy && (
                <div>
                  <strong>Reviewed By</strong>
                  <IsomorphicLink
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`/lookup/${taskReview.reviewedBy._id}`}
                  >
                    {taskReview.reviewedBy.email}
                  </IsomorphicLink>
                </div>
              )}

              {!!taskReview.reviewedAt && (
                <div>
                  <strong>Reviewed At</strong>
                  <Tooltip
                    placement={'top'}
                    title={new Date(taskReview.reviewedAt).toLocaleString()}
                  >
                    <span>{moment(taskReview.reviewedAt).fromNow()}</span>
                  </Tooltip>
                </div>
              )}
              {typeof taskReview.payoutAmountHundredthsOfCents !== 'undefined' && (
                <div>
                  <strong>Payout</strong>
                  <span>${(taskReview.payoutAmountHundredthsOfCents / 10000).toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TaskReviewPreview;
